///<reference path='pixilayer.ts' />
///<reference path="../utils/transform.ts"/>
///<reference path="../utils/general.ts"/>

declare var Hammer : any;

module agi.expressions.card.layer{


    import TransformUtils = agi.expressions.card.utils.TransformUtils;
    import GeneralUtils = agi.expressions.card.utils.GeneralUtils;

    export interface MinimalPoint{
        X_OFFSET : number,
        Y_OFFSET : number
    }
    export interface DomInfo{
        X_OFFSET: number,
        Y_OFFSET: number
        SCALE_FACTOR: number
    }

    /**
     * Interface describing information about whether the focus is out of bounds, where the bounds are the screen
     */
    export interface OutOfBoundsInfo{
        outInfo: PIXI.Point;
        globalFocusSize: PIXI.Point
        globalFocusUpperLeft : PIXI.Point
    }

    /**
     * Represents a configuration object for the drop shadow
     */
    export interface ShadowSettings{
        sprite: PIXI.Container; //The actual shadow sprite
        padding: number; //Amount of padding to apply to the shadow on each side. (Without this, the shadow is equal to the focus in dimensions)
        imagePixelPadding: number; //Amount of pixels from each side where there is transparency on the shadow graphic.
    }

    /**
     * Draggable PIXI Container which is capable of zooming, smoothly dragging, and snapping back to the viewport.
     * @requires PIXI
     * @requires Hammer for drag gestures
     * @requires GSAP
     */
    export class DraggableLayer extends PIXI.Container{
        //Zoom
        private minZoom:number=1;
        private maxZoom:number=3;        
        //Deltas which allow for extra zoom changes
        private zoomStretchMinDelta:number=0.15;
        private zoomStretchMaxDelta:number=0.5;
        //Duration of bounce tweens
        private bounceDuration:number = 0.65;
        //Smallest allowable speed to base intertia calculations off of
        private minimumEndingSpeed = 150;
        /**
         * If true, this means that the container will only be able to be dragged when the pointer is over this layer
         * and not being blocked by a layer above it. 
         */
        private onlyDragWhenClickable : boolean = true;

        //hammer-specific vars
        private hammerLastDelta = new PIXI.Point(0, 0);
        private hammerLastRotation = 0;
        private hammerStartingScale = 1;
        private hammerLastPivot = new PIXI.Point(0, 0);
        private hammerAnchor:PIXI.Sprite;
        private isPanning:boolean=false;
        private isPanningDelay:boolean=false;
        private isPanningDelayTimeout;
        private hammerStartFired = false;


        //The main sprite this container is focused on. In other words, the sprite that the bounds of this container is based on
        private focus:PIXI.Container;
        private shadowSettings : ShadowSettings;
        private renderer:PIXI.WebGLRenderer|PIXI.CanvasRenderer;

        private allowUserInput:boolean = true;
        private prevAllowUserInput = true; //@see temporarilyDisableInteractivity and resumeNormalInteractivity

        private domInfoGetter : () => DomInfo;
        private parentBoundsGetter : () => PIXI.Rectangle;        
        private hitDetectionOnClick : () => void;

        //This determines if this object can be detected by hammer right now.
        private touchable = false;
        private isSnappingBack = false;
        private mouseIsOver = false;
        private isTemporarilyDisablingUserInput = false;
        private hammerStartedAboveMinZoom = false;

        //Number that we'll multiply the raw scroll deltaY by when scaling. Therefore, the smaller this is, the less effective scrolling will be.
        public scrollMultiplier = 0.0007;
        //should scene be able to pan at min zoom level?
        public preventPanAtMin:boolean=true;
        public preventDefaultScrollEventsWhenMouseOver = true;
        //Events
        public onsnapback : Array<()=>void> = [];


        /**
         * Construct a new DraggableLayer.
         * @param focus The Sprite that we're focused on and that the edge detection methods will be based on
         * @param renderer The PIXI renderer currently be used
         * @param parent The parent that we'll add this container to. In order for bounds to be based on this, it must have a non-zero area **before** this layer gets added as a child to it. 
         * Otherwise, we'll just use the renderer for bounds.
         * @param domInfoGetter This should be a function which returns an object satisfying the DomInfo interface. 
         * Because this is a getter, it will be up-to-date whenever this layer uses it. Additionally, it allows usage of a Config object or a converted dom rectangle. 
         * See draggablelayer.test.ts for usage examples.
         * @param shadowSettings Optional reference to a configuration object representing the shadow settings.
         */
        constructor(focus: PIXI.Container, renderer:PIXI.WebGLRenderer|PIXI.CanvasRenderer, parent:PIXI.Container, domInfoGetter: () => DomInfo, shadowSettings:ShadowSettings=null){
            super();
            this.focus = focus;
            this.renderer = renderer;

            this.addChild(focus);

            let parentUnallocated = parent.width * parent.height == 0;
            if (!parentUnallocated && (this.width != parent.width || this.height != parent.height))
                throw "Focus dimensions must be equal to parent's dimensions";

            parent.addChild(this);            
            if (parent.parent)
                parent.updateTransform();

            
            if (parentUnallocated){
                this.useRendererForHitDetection();
            }
            else{
                this.parentBoundsGetter = () => this.parent.getBounds();
            }


            if (shadowSettings){      
                this.shadowSettings = shadowSettings;
                this.prepareShadow(this.shadowSettings);
                this.addChildAt(this.shadowSettings.sprite, 0);
                this.shadowSettings.sprite.updateTransform();
                this.shadowSettings.sprite.alpha = 0; //Start off being invisible.              
            }

            this.domInfoGetter = domInfoGetter;

            this.setupHammer();
            this.setupMouseWheel();
            this.hitDetectionOnClick = () => this.touchable = true;
            this.setOnlyDragWhenClickable(this.onlyDragWhenClickable);
            
            this.interactive = true;

            this.on("mouseover", () => this.mouseIsOver = true);
            this.on("mouseout", () => this.mouseIsOver = false);



            //Attach an event handler for onsnapback to resume normal interactivity 
            //This gets reenabled after zooming out gets called and disables interactivity
            this.onsnapback.push(this.resumeNormalUserInput.bind(this));
        }

        /** 
         * Get the raw state of whether this layer is panning. 
         * Note that if you'd like to use this to only allow clicks when not panning, you should use getDelayedIsPanning.
         */
        public getIsPanning() : boolean { return this.isPanning; }
        /**
         * Get a "smoothed out" version of isPanning.
         * This is nearly identical to getIsPanning, but is set to false 50 milliseconds **after** pan events.
         * The main benefit of this is that you can now detect a click, and not accept it if this is true because of the small delay.
         */
        public getDelayedIsPanning() : boolean { return this.isPanningDelay; }

        /**
         * Sets the min and max zoom deltas. These allow for extra zoom below minZoom and past maxZoom on touch devices only.
         * These are directionless deltas, so don't use negative values.
         */
        public setZoomStretchDeltas(minDelta : number, maxDelta: number){
            if (minDelta < 0 || maxDelta < 0)
                throw "Values must be greater than or equal to zero";
            this.zoomStretchMinDelta = minDelta;
            this.zoomStretchMaxDelta = maxDelta;
        }
        /**
         * Returns a **copy** of zoomStretchDeltas as a PIXI.Point
         */
        public getZoomStretchDeltas() : PIXI.Point{
            return new PIXI.Point(this.zoomStretchMinDelta, this.zoomStretchMaxDelta);
        }

        /** bounceDuration refers to how long scaling and snapback tweens last.*/
        public getBounceDuration() : number{ return this.bounceDuration; }
        /** @see getBounceDuration */
        public setBounceDuration(value){
            if (value < 0)
                throw "bounce duration must be greater than or equal to zero";
            this.bounceDuration = value;
        }

        /**
         * Set the minimum and maximum zoom levels
         * @param min min zoom 
         * @param max max zoom
         */
        public setZoomLevels(min:number, max:number){
            this.minZoom = min;
            this.maxZoom = max;
            this.detectEdges(true);
            if (this.minZoom > this.maxZoom)
                throw "minZoom is more than maxZoom!";
        }

        /** Refers to the minimum allowable speed for the layer to have **after** it has been dragged, provided speed is not already zero */
        public getMinimumEndingSpeed(){ return this.minimumEndingSpeed; }
        /** @see getMinimumEndingSpeed */        
        public setMinimumEndingSpeed(value){
            if (value < 0)
                throw "minimumEndingSpeed must be greater than or equal to zero";
            this.minimumEndingSpeed = value;
        }

        private useRendererForHitDetection(){
            this.parentBoundsGetter = () => new PIXI.Rectangle(0,0,this.renderer.width,this.renderer.height);
        }

        private setupMouseWheel(){
            document.addEventListener('wheel', e=>{
                this.updateFocusHitArea();
                if (this.preventDefaultScrollEventsWhenMouseOver && this.mouseIsOver)
                    e.preventDefault();
                this.wheel(e);
            }, false);
        }

        private setupHammer(){
            //Hammer - listens for multitouch
            var hammertime = new Hammer(this.renderer.view);
            var pan = hammertime.get("pan")
            pan.set({direction: Hammer.DIRECTION_ALL, threshold: 0});
            var pinch = hammertime.get("pinch");
            pinch.set({enable: true});
            pinch.recognizeWith(pan);
            hammertime.on("panstart pinchstart rotatestart", e=>this.hammerStart(e));
            hammertime.on("panmove pinchmove rotatemove", e=> this.hammerMove(e));
            hammertime.on("panend pancancel pinchend rotateend",  e=>this.hammerEnd(e));   
        }

        /**
         * This refers to the ability to allow layer movement based on if pointer is actually over this layer. 
         * @param value If true, we can only drag this layer when the pointer is over it. 
         * If false, we can drag this layer as long as the pointer is over the canvas. 
         */
        public setOnlyDragWhenClickable(value){
            this.onlyDragWhenClickable = value;
            //Make sure we don't double up on listeners
            this.removeListener("mousedown", this.hitDetectionOnClick);
            this.removeListener("touchstart", this.hitDetectionOnClick);
            if (this.onlyDragWhenClickable){
                this.setupHitDetection();
            }
            else{
                this.touchable = true;
            }
        }
        /** @see setOnlyDragWhenClickable */
        public getOnlyDragWhenClickable(){ return this.onlyDragWhenClickable; }

        /**
         * Setup the event listeners to get an accurate reading of the touchable member variable
         */
        private setupHitDetection(){
            this.updateFocusHitArea();            
            if (this.onlyDragWhenClickable) //Extra precaution
            {
                this.on("mousedown", this.hitDetectionOnClick);
                this.on("touchstart", this.hitDetectionOnClick);
                this.touchable = false; //Don't allow dragging/zooming onclick is called
            }
        }

        private updateFocusHitArea(){
            if (!this.parent)
                return;
            if (!this.hitArea)
                this.hitArea = new PIXI.Rectangle();
            let rect = <PIXI.Rectangle>this.hitArea;
            let pb = this.parentBoundsGetter();
            let ul = this.toLocal(new PIXI.Point(pb.x, pb.y));
            let wh = TransformUtils.inverseTransformDirection(new PIXI.Point(pb.width, pb.height), this.worldTransform);
            rect.x = ul.x; rect.y = ul.y;
            rect.width = wh.x; rect.height = wh.y;
            this.updateTransform();
        }

        /**
         * Position and scale the shadow to fit nicely behind the focus.
         * @param settings ShadowSettings
         */
        private prepareShadow(settings : ShadowSettings){
            if (!settings)
                return;
            let shadow = settings.sprite;
            shadow.width = this.focus.width + 2 * settings.padding; 
            //Account for the unused transparent pixels that were scaled 
            shadow.width += settings.imagePixelPadding * 2 * shadow.scale.x;
            shadow.height = this.focus.height + 2 * settings.padding;
            shadow.height += settings.imagePixelPadding * 2 * shadow.scale.y;                
            shadow.position.set(this.focus.width/2 - shadow.width/2, this.focus.height/2 - shadow.height/2);
        }

        /**
         * Change the focus of the draggable layer and adjust the shadow to fit it (if there is a shadow at all)
         * @param newFocus 
         */
        public changeFocus(newFocus:PIXI.Sprite){
            let newFocusBounds = newFocus.getBounds();
            let pb = this.parentBoundsGetter();
            if (newFocusBounds.width != pb.width || newFocusBounds.height != pb.height)
                throw "new focus sprite's dimensions are not equal to the parent's dimensions";
            
            this.removeChild(this.focus);
            this.focus = newFocus;
            this.addChild(this.focus);
            this.prepareShadow(this.shadowSettings); //This method will handle null settings check
        }
        /** Get the focus of this container. It is not recommended to change the properties of the reference this method returns. */
        public getFocus(){ return this.focus; }

        private pannable() : boolean{
            //If we started zoomed in and min
            if (this.hammerStartedAboveMinZoom && this.zoomStretchMinDelta == 0)
                return true;
            return (this.scale.x != this.minZoom);
        }

        // Hammer 
        private hammerStart(ev:any){
            this.updateFocusHitArea();
            if (!this.allowUserInput || !this.touchable)
                return;
            this.hammerLastDelta.set(ev.deltaX, ev.deltaY);
            this.hammerLastRotation = ev.rotation;
            this.hammerStartingScale = this.scale.x;
            //We need to kill all current tweens so momentum does not get in the way of dragging
            this.clearTranslationTweens();
            this.clearScalingTweens();
            this.hammerStartFired = true;
            this.hammerStartedAboveMinZoom = this.scale.x > this.minZoom;
            

            if (this.shadowSettings && this.shadowSettings.sprite)
            {
                //Tween shadow alpha to 1
                TweenLite.killTweensOf(this.shadowSettings.sprite);
                TweenLite.to(this.shadowSettings.sprite, 0.35, {alpha:1});
            }
        }
        private hammerMove(ev:any){
            this.updateFocusHitArea();
            if (!this.allowUserInput || !this.hammerStartFired)
                return;
                
            this.renderer.view.style.cursor="-webkit-grabbing";
            this.renderer.view.style.cursor="-moz-grabbing";
            this.renderer.view.style.cursor="grabbing";
            
            //Prevent pan at min zoom if the flag is true.
            if((this.preventPanAtMin && this.pannable()) 
                || !this.preventPanAtMin){
                var delta = new PIXI.Point(
                    ev.deltaX - this.hammerLastDelta.x,
                    ev.deltaY - this.hammerLastDelta.y
                );
                this.hammerLastDelta.set(ev.deltaX,ev.deltaY);
                //We need to convert this world delta to a local delta
                delta = TransformUtils.inverseTransformDirection(delta, this.parent.worldTransform);
                this.position.x += delta.x/this.domInfoGetter().SCALE_FACTOR;
                this.position.y += delta.y/this.domInfoGetter().SCALE_FACTOR;
                this.isPanning=true;
                this.isPanningDelay = true;
            }  
            //Once pan is done (if any), re-center the pivot
            this.updatePivot(ev);
            // Scale for pinch to zoom
            this.scale.x = this.hammerStartingScale * ev.scale;
            this.scale.y = this.hammerStartingScale * ev.scale;

            // clamp scale to min or max if over the limit
            if(this.scale.x<this.minZoom-this.zoomStretchMinDelta){
                this.scale.set(this.minZoom-this.zoomStretchMinDelta,this.minZoom-this.zoomStretchMinDelta);
            } else if (this.scale.x>this.maxZoom+this.zoomStretchMaxDelta){
                this.scale.set(this.maxZoom+this.zoomStretchMaxDelta,this.maxZoom+this.zoomStretchMaxDelta);
            }

            // Rotation
            // Disabled but possible. Need to re-write hammerEnd.
            /*var rotationDeltaDegrees = ev.rotation - this.hammerLastRotation;
            var rotationDelta = rotationDeltaDegrees * (Math.PI/180);
            this.rotation += rotationDelta;
            this.hammerLastRotation = ev.rotation;*/
        }
        private hammerEnd(ev){           
            this.updateFocusHitArea();
            if (!this.allowUserInput || !this.hammerStartFired )
                return; 
            
            let wasPanning = this.isPanning;
            this.isPanning=false;
            this.renderer.view.style.cursor="default";

            this.hammerStartFired = false; //To make sure drag isn't called before start in some weird circumstances
            if (this.onlyDragWhenClickable)
                this.touchable = false;

            this.isPanningDelayTimeout && clearTimeout(this.isPanningDelayTimeout);
            this.isPanningDelayTimeout = setTimeout(()=>this.isPanningDelay=false, 50);

            if (this.shadowSettings && this.shadowSettings.sprite)
            {           
                //Hide the shadow
                TweenLite.killTweensOf(this.shadowSettings.sprite);
                TweenLite.to(this.shadowSettings.sprite, 0.35, {alpha:0});
            }


            
            let obInfo = this.getOutOfBoundsInfo();
            if (obInfo.outInfo.x != 0 || obInfo.outInfo.y != 0 || ev.pointers.length > 1)
            {
                this.detectEdges();
                return;
            }
            
            // Momentum - Drag the scene and let go, scene keeps inertia for a small time
            //Velocity is in px/ms => convert this to px/s
            let vel = new PIXI.Point(ev.velocityX * 1000 , ev.velocityY * 1000 );
            //Get the magnitude of velocity
            let speed = Math.sqrt(Math.pow(vel.x,2) + Math.pow(vel.y,2));

            if (speed < this.minimumEndingSpeed){
                vel.set(vel.x / speed, vel.y / speed);
                vel.set(vel.x * this.minimumEndingSpeed, vel.y * this.minimumEndingSpeed);
                speed = this.minimumEndingSpeed;
            }

            //Accelerate 
            let accel = new PIXI.Point(-vel.x, -vel.y);
            //accel.x *= 1.6; accel.y *= 1.6;

            let dt = 1;
            //Displacement function, d = 1/2at^2 + vt
            let displacement = (a:number,v:number) => (0.5 * a * dt * dt) + (v * dt); 
            let disp = new PIXI.Point(
                displacement(accel.x, vel.x),
                displacement(accel.y, vel.y)
            );
            //Now convert to local space
            disp = TransformUtils.inverseTransformDirection(disp, this.parent.worldTransform);

            //We do this here rather than in detectEdges because we'd have to clearTranslationTweens in detectEdges to do the equivalent,
            //and that would stop snapBack from doing its job since we're calling detectEdges each frame.
            if (!wasPanning) //don't do inertia stuff if we weren't even panning before
                disp.set(0,0);

            //Tween to the displaced position, clamping to screen each frame
            TweenLite.to(this.position, dt, {x: `+=${disp.x}`, y:`+=${disp.y}`, ease:Power3.easeOut, onUpdate: () => this.detectEdges(true)});
            // Zoom buttons UI?
        }

        private wheel(ev:WheelEvent){
            //console.log(!this.allowUserInput + ", " + this.isPanning + ", " + this.isSnappingBack + ", " + !this.mouseIsOver);
            if (!this.allowUserInput || this.isPanning || this.isSnappingBack || !this.mouseIsOver)
                return;

            //Prevent dragging conflicts
            this.clearTranslationTweens();
            this.updatePivot(ev);
            
            var dY = ev.deltaY;

            /**The WheelEvent has three types of deltaModes - only two ever seemed to be used normallly though: 
             * DOM_DELTA_PIXEL and DOM_DELTA_LINE. The latter is only used in Firefox. DELTA_PAGE seems to be a windows-only option.
             * Regardless, we'll need to check for this and convert whatever value we get to pixels.
             */
            let conversionMultiplier;
            switch (ev.deltaMode){
                case ev.DOM_DELTA_LINE:
                    conversionMultiplier = GeneralUtils.SCROLL_LINE_HEIGHT;
                    break;
                case ev.DOM_DELTA_PAGE:
                    conversionMultiplier = GeneralUtils.SCROLL_PAGE_HEIGHT;
                    break;
                //Intentional fall-through
                case ev.DOM_DELTA_PIXEL:
                default:
                    conversionMultiplier = 1;
            }

            let step = dY * this.scrollMultiplier * conversionMultiplier * -1;
            this.scale.x += step;
            this.scale.y += step;
            if(this.scale.x>this.maxZoom){
                this.scale.x=this.maxZoom;
                this.scale.y=this.maxZoom;
            }
            if(this.scale.x<this.minZoom){
                this.scale.x=this.minZoom;
                this.scale.y=this.minZoom;
            }
            //Continuously detectEdges and get back into view when scrolling
            this.detectEdges(true);

            //Make sure shadow is visible
            if (this.shadowSettings && this.shadowSettings.sprite)
                this.shadowSettings.sprite.alpha = 1; //TODO tween to this instead
        }


        /**
         * Get info regarding where the **focus** is out bounds, where bounds refers to the parent.
         */
        private getOutOfBoundsInfo() : OutOfBoundsInfo{
            //Update this transform so focus, which is a child, can also be updated appropriately.
            //Issues can occur w/o this when we Tween this container's scale and act onComplete b/c the transform matrix may not have been updated at the end of GSAP's animation loop.
            this.parent.parent ? this.parent.updateTransform() : this.updateTransform();    
            let bounds = this.focus.getBounds();
            let globalFocusUpperLeft = new PIXI.Point(bounds.x, bounds.y);
            let width = bounds.width;
            let height = bounds.height;
            let info : OutOfBoundsInfo = {
                outInfo: new PIXI.Point(),
                globalFocusSize: new PIXI.Point(width, height),
                globalFocusUpperLeft
            };
            let pb = this.parentBoundsGetter();
            if(globalFocusUpperLeft.x>pb.x){
                info.outInfo.x = 1;
            }
            else if(globalFocusUpperLeft.x + width < pb.x + pb.width){
                info.outInfo.x = -1;
            }
            if(globalFocusUpperLeft.y>pb.y){
                info.outInfo.y = 1;
            }
            else if(globalFocusUpperLeft.y + height < pb.y + pb.height){
                info.outInfo.y = -1;
            }
            return info;
        }


        /**
         * Do a check to see if the focus is out of bounds, and if it is, move it back in bounds. 
         * Also checks for scale being out of bounds (> than maxZoom or < than minZoom) and will fix that.
         * @param abruptly Should we move the focus back in bounds immediately? If not, we'll use GSAP to lerp into place. Defaults to false.
         */
        public detectEdges(abruptly=false) {

            /**
             * Make the hogContainer snap back into view if it is out of view
             * @param ease 
             */
            let snapBack = (ease:any=Back.easeOut, bounceDur=this.bounceDuration) => {
                let obInfo = this.getOutOfBoundsInfo();

                let globalDesiredPivot = this.toGlobal(this.pivot);
                let globalTweenTarget = this.parent.toGlobal(this.position);
                let pb = this.parentBoundsGetter();
                if(obInfo.outInfo.x > 0){
                    globalDesiredPivot.x = obInfo.globalFocusUpperLeft.x;
                    globalTweenTarget.x = pb.x;
                }
                else if(obInfo.outInfo.x < 0){
                    globalDesiredPivot.x = obInfo.globalFocusUpperLeft.x + obInfo.globalFocusSize.x;
                    globalTweenTarget.x = pb.x + pb.width;
                }
                if(obInfo.outInfo.y > 0){
                    globalDesiredPivot.y = obInfo.globalFocusUpperLeft.y;
                    globalTweenTarget.y = pb.y;
                }
                else if (obInfo.outInfo.y < 0){
                    globalDesiredPivot.y = obInfo.globalFocusUpperLeft.y + obInfo.globalFocusSize.y;
                    globalTweenTarget.y = pb.y + pb.height;
                }    
                let tweenConfig : any = {ease};
                let desiredPivot = this.toLocal(globalDesiredPivot);
                this.updatePivotAndMaintainPosition(desiredPivot);

                let tweenTarget = this.parent.toLocal(globalTweenTarget);
                //Check each axis separately to determine if either of them actually need to tween somewhere.
                if (!GeneralUtils.approximately(tweenTarget.x, this.position.x))
                    tweenConfig.x = tweenTarget.x;
                if (!GeneralUtils.approximately(tweenTarget.y, this.position.y))
                    tweenConfig.y = tweenTarget.y;

                let duration = 0;
                //Only broadcast onsnapback if we're not going there abruptly. 
                //Otherwise it would be called many times and wouldn't be helpful to know about for observers
                if (!abruptly){
                    tweenConfig.onComplete = e=>{
                        this.onsnapback.forEach(observer => observer());
                        this.isSnappingBack = false;
                    };
                    //We're only snapping back if we're not doing this abruptly, otherwise it's instantaneous
                    if ("x" in tweenConfig || "y" in tweenConfig){
                        //Confirm that we're actually going somewhere - very possible that we're not!
                        this.isSnappingBack = true;
                    }
                    duration = bounceDur;
                }
                tweenConfig.onUpdate = () => this.updateFocusHitArea(); 
                TweenLite.to(this.position, duration, tweenConfig);
            }

            let scaleBackIn = (target) => {
                //If scale is too big, we may be out of bounds, or we may not. So we need snapBack to detect this for us.
                let scaleEase = abruptly ? Power2.easeOut : Back.easeOut;//We don't want Back.easeOut if this may be called each frame
                let tweenConfig : any = {x:target,y:target,ease:scaleEase};

                if (!abruptly){
                    //If we're not doing this abruptly, it's probably not being called each frame
                    //So we need to make sure we're in bounds all the time as we're scaling in
                    tweenConfig.onUpdate = () => snapBack(Power0.easeNone, this.bounceDuration/4);//We don't want Back.easeOut each frame!
                }
                else{
                    //If we are doing this abruptly, this is probably being called each frame, so this snapBack call is similar to being called onUpdate 
                    snapBack();   
                }
                TweenLite.to(this.scale, this.bounceDuration,tweenConfig);
            }

            if(this.scale.x<this.minZoom){
                scaleBackIn(this.minZoom);
            } 
            else if (this.scale.x > this.maxZoom){
                scaleBackIn(this.maxZoom);
            }
            //Otherwise, just check if we're positionally out of bounds and fix it.
            else{
                snapBack();
            }
        }

        /**
         * Zoom out the draggable container to fully fit the screen, and temporarily disable interactivity while this is occuring.
         * @param hideBlankSpace indiciates if we should continuously make the container fit into the view so no blank space shows
         */
        public zoomOut(hideBlankSpace=true){
            if(this.scale.x!=this.minZoom){    
                this.clearScalingTweens();
                this.clearTranslationTweens();
                this.temporarilyDisableUserInput();

                //Note that if scale is < minZoom, the tween below will get canceled out by detectEdges.
                //But this is ok because it will produce the same result -> getting back to minZoom.

                let ob = this.getOutOfBoundsInfo();
                let outOfBounds = ob.outInfo.x != 0 || ob.outInfo.y != 0;

                //We need to to do this because when zoomed in too far, detectEdges will try to fix the scale itself. 
                //We, however, want to zoom out completely, not a just back to maxZoom!
                if (this.scale.x > this.maxZoom)
                {
                    this.scale.set(this.maxZoom, this.maxZoom);
                    //If some other class zooms this layer out and then immediately uses the layer's transform,
                    //it will be out-of-date unless we update it.
                    //We only need to do this here because in any other instance, we're tweening, which is not instantaneous, 
                    //So pixi will just handle the update
                    this.parent && this.updateTransform();
                }
                //We're going to make this layer think we never even started dragging, that way we won't snap in after the tween
                this.hammerStartFired = false; 
                TweenLite.to(this.scale,0.65,{x:this.minZoom,y:this.minZoom,ease:Sine.easeOut, onComplete: ()=>{
                    this.scale.set(this.minZoom, this.minZoom); //Set it explicitly in case it doesn't make it
                    //Get back in bounds  
                    if (!hideBlankSpace)
                        this.detectEdges(false); 
                    //Make sure onsnapback fires.
                    if (hideBlankSpace && !outOfBounds)
                        this.onsnapback.forEach(f => f());
                }, onUpdate: ()=>{
                    if (hideBlankSpace){
                        this.detectEdges(!outOfBounds);
                    }
                }});                    
            }
        }

        /**
         * Disable interactivity with this draggable layer.
         * Use in conjunction with resumeNormalInteractivity to resume back to what is was before this.
         */
        public temporarilyDisableUserInput(){
            //If we've already got one of these going that hasn't completed, overwrite it
            if (this.isTemporarilyDisablingUserInput){
                this.allowUserInput = this.prevAllowUserInput; //Revert anything that was there before
            }
            this.isPanning = false;
            this.isPanningDelay = false;
            this.prevAllowUserInput = this.allowUserInput;
            this.allowUserInput = false;
            this.isTemporarilyDisablingUserInput = true;
        }
        /**
         * Set interactivity to whatever it was when temporariliyDisableInteractivity was called.
         */
        public resumeNormalUserInput(){
            //Do nothing if temporarilyDisableInteractivity hasn't been called first
            if (!this.isTemporarilyDisablingUserInput)
                return;
            this.allowUserInput = this.prevAllowUserInput;
            this.isTemporarilyDisablingUserInput = false;
        }

        /**
         * Set whether or not this layer is draggable and zoomable.
         * @param userInput 
         */
        public setUserInput(userInput: boolean){
            this.allowUserInput = userInput;
            this.prevAllowUserInput = userInput;
        }

        /**
         * Reset the draggable layer to minimum zoom and to position of (0,0) in global space.
         */
        public reset(){
            this.clearTranslationTweens();
            this.clearScalingTweens();
            this.scale.set(this.minZoom, this.minZoom);
            this.updateTransform();
            let pb = this.parentBoundsGetter();
            let globalOrigin = new PIXI.Point(pb.x, pb.y);
            let focusBounds = this.focus.getBounds();
            this.pivot.copy(this.toLocal(new PIXI.Point(focusBounds.x, focusBounds.y)));
            this.position.copy(this.parent.toLocal(globalOrigin));
        }

        private getCenter(ev:any) {
            if(ev.center!=undefined){
                //touch version
                var globalCenter = new PIXI.Point(
                    (ev.center.x-this.domInfoGetter().X_OFFSET)/this.domInfoGetter().SCALE_FACTOR,
                    (ev.center.y-this.domInfoGetter().Y_OFFSET)/this.domInfoGetter().SCALE_FACTOR
                );
                return this.toLocal(globalCenter);
            }
            else{
                //mousewheel version
                var globalCenter = new PIXI.Point(
                    (ev.x - this.domInfoGetter().X_OFFSET)/this.domInfoGetter().SCALE_FACTOR,
                    (ev.y - this.domInfoGetter().Y_OFFSET)/this.domInfoGetter().SCALE_FACTOR
                );
                return this.toLocal(globalCenter);

            }
        }
        /**
         * Update the pivot to the mouse or last tapped/zoomed position without causing a visual change of the hogContainer
         * @param ev Hammer event
         * @param scaleFactor CSS scale factor
         * @param oX x offset
         * @param oY y offset
         * @param point Specifying this will cause this to set the pivot to this point. Call updatePivotAndMaintainPosition to do this.
         */
        private updatePivot(ev:any, point:PIXI.Point=null) 
        {
            let localCenter = point ? point : this.getCenter(ev);
            TransformUtils.setPivotAndMaintainVisualPosition(localCenter, this);
        }

        /**
         * Update the pivot and adjust the position so the hog container does not visually move
         * @param newPivot 
         */
        private updatePivotAndMaintainPosition(newPivot : PIXI.Point){
            this.updatePivot(null,newPivot);
        }


        /**
         * Clear all active translation tweens. Typically call this if you want to make sure tweens do not conflict.
         */
        private clearTranslationTweens(){
            TweenLite.killTweensOf(this.position);
            this.isSnappingBack = false; //We can't be snapping back into view right now if there are no tweens right now.
            this.resumeNormalUserInput();
        }
        /**
         * Clear all active scaling tweens. Typically call this if you want to make sure tweens do not conflict.
         */
        private clearScalingTweens(){
            TweenLite.killTweensOf(this.scale);
            this.isSnappingBack = false; //We can't be snapping back into view right now if there are no tweens right now.   
            this.resumeNormalUserInput();         
        }

        /**
         * Manually move the draggable layer. This is typically used for testing purposes, so be careful with this.
         * @param amt Amount to move local to the parent
         */
        public move(amt : PIXI.Point)
        {
            amt = this.parent.toLocal(amt);
            let simEv = {deltaX: amt.x, deltaY: amt.y, x:0, y:0, scale: 1, rotation: 0, pointers:[] };
            this.hammerStartFired = true;
            this.hammerStartingScale = this.scale.x;
            this.hammerMove(simEv);
            this.hammerEnd(simEv);
        }
    }
}